import cn from 'classnames';
import { type FC, useState } from 'react';

import { useTreatmentModal } from '@/components/Studies/StudyGroups/Treatments/useTreatmentModal';
import { classificationTypes } from '@/components/Studies/Treatments/Treatment.utils';
import { errorToast } from '@/helpers';
import { _isNil } from '@/littledash';
import type { MetadataField } from '@/model/Metadata.model';
import type { Study } from '@/model/Study.model';
import type { ClassificationType, Treatment } from '@/model/Treatment.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useFetchCollection } from '@/support/Hooks/fetch';
import useMountedState from '@/support/Hooks/fetch/useMountedState';

import type { OverviewTreatmentRow } from '../../OverviewAccordion.model';
import OverviewTreatmentsTable from './OverviewTreatmentsTable';

interface EditableOverviewTreatmentsTableProps {
  rows: Array<OverviewTreatmentRow>;
  study: Study;
  type: ClassificationType;
  onTreatmentSaved?: () => void;
}

const listedClassifications: Record<ClassificationType, Array<ClassificationType>> = {
  treatment: ['treatment', 'pre_treatment'],
  pre_treatment: ['treatment', 'pre_treatment'],
  disease_induction: ['disease_induction'],
};

/**
 * The overview payload only carries display strings, so a label click loads the
 * full treatment before handing it to the study-groups edit modal.
 */
const EditableOverviewTreatmentsTable: FC<EditableOverviewTreatmentsTableProps> = ({
  rows,
  study,
  type,
  onTreatmentSaved,
}) => {
  const [loadingTreatment, setLoadingTreatment] = useState<boolean>(false);
  const isMounted = useMountedState();

  const { collection: metadata } = useFetchCollection<MetadataField>({
    collectionType: 'metadataGlossaries',
    queryParams: { filter_type: classificationTypes[type].metadata },
  });

  const { addingTreatment, updatingTreatment, openTreatmentModal } = useTreatmentModal({ study, metadata, type });

  const { invoke: fetchTreatments } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/treatments',
    invokeOnInit: false,
    options: { onError: { toast: false, capture: true, throw: true, slug: 'overview-treatment-load' } },
  });

  const loadTreatment = async (apiId: string): Promise<Treatment | undefined> => {
    const response = await fetchTreatments({
      path: { studyApiId: study.api_id },
      query: {
        include: ['study_groups', 'metadata'],
        classification: listedClassifications[type],
        perPage: -1,
      },
    });

    if (response.type !== 'success') {
      return undefined;
    }

    return (response.body.data as unknown as Array<Treatment>).find(
      ({ api_id: treatmentApiId }) => treatmentApiId === apiId
    );
  };

  const onLabelClick = async (row: OverviewTreatmentRow): Promise<void> => {
    setLoadingTreatment(true);
    let treatment: Treatment | undefined;
    try {
      treatment = await loadTreatment(row.api_id);
    } catch {
      treatment = undefined;
    } finally {
      if (isMounted()) {
        setLoadingTreatment(false);
      }
    }

    if (_isNil(treatment)) {
      errorToast(`Could not load ${classificationTypes[type].key}`);
      return;
    }

    const saved = await openTreatmentModal(treatment);
    if (saved) {
      onTreatmentSaved?.();
    }
  };

  return (
    <div className={cn({ ui__disabled: loadingTreatment || addingTreatment || updatingTreatment })}>
      <OverviewTreatmentsTable rows={rows} type={type} onLabelClick={onLabelClick} />
    </div>
  );
};

export default EditableOverviewTreatmentsTable;
